import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CallMadeRoundedIcon from '@mui/icons-material/CallMadeRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import mqtt from "mqtt";
import { useParams } from 'react-router-dom';
import { getAllVM } from '../../api/routes';

const MQTT_OPTIONS = {
  username: 'operator_ws',
  password: 'Q4ddoje88D_u1cUsTUHwI30dtbwdj6LP',
  rejectUnauthorized: true,
  reconnectPeriod: 2000,
};

const STATUS_CARD_SX = {
  width: '85vw',
  maxWidth: '1700px',
  minHeight: '70vh',
  bgcolor: '#4f7f35',
  borderRadius: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: 2,
  color: '#f5fff0',
  boxShadow: '0 30px 80px rgba(15, 26, 20, 0.35)',
};

const CHECKOUT_CARD_SX = {
  width: '85vw',
  maxWidth: '1700px',
  minHeight: '80vh',
  bgcolor: '#5a7f33',
  borderRadius: 0,
  border: '4px solid #38551f',
  boxShadow: '0 30px 80px rgba(15, 26, 20, 0.35)',
  overflow: 'hidden',
};

export default function LocalMachine() {
    const { vm_id } = useParams();
  const client = useMemo(() => mqtt.connect('wss://ivm9.in:9001', MQTT_OPTIONS), []);

  const [tableData, setTableData] = useState([]);
  const [callStatus, setCallStatus] = useState(null);
  const [dispensePhase, setDispensePhase] = useState('idle');
  const dispenseTimerRef = useRef(null);

  const price_total = tableData.reduce((accumulator ,item) => {
    return accumulator += item.price;
  }, 0);

  const formatCurrency = (value) => Number(value || 0).toFixed(2);
  const amountWords = (value) => {
    const rounded = Math.round(Number(value || 0));
    if (rounded === 0) return "Zero Rupees Only";
    return `${rounded} Rupees Only`;
  };

  const emptyRows = Math.max(0, 8 - tableData.length);

  useEffect(() => {
    const hydrateInitialCallState = async () => {
      try {
        const vms = await getAllVM();
        const currentVm = vms.find((vm) => vm.guid === vm_id);
        if (currentVm) {
          setCallStatus(parseInt(currentVm.call_status, 10));
        } else {
          setCallStatus(0);
        }
      } catch (error) {
        console.log("initial call state error", error);
        setCallStatus(0);
      }
    };

    hydrateInitialCallState();

    const localTopic = `vm/pi/local/${vm_id}`;
    const callTopic = `vm/${vm_id}/call/status`;
    const conveyorTopic = `con_belt/vendingmachine/${vm_id}`;
    client.subscribe(localTopic);
    client.subscribe(callTopic);
    client.subscribe(conveyorTopic);

    const onMessage = (topic, data) => {
      if (topic === localTopic) {
        const LocalMachineData = JSON.parse(data);
        setTableData(LocalMachineData);
        return;
      }

      if (topic === callTopic) {
        try {
          const payload = JSON.parse(data.toString());
          if (payload.type === "call_status" && payload.vm_id === vm_id) {
            const nextCallStatus = parseInt(payload.value, 10);
            setCallStatus((currentCallStatus) => {
              // Keep the local session stable if a late device-side ring event arrives.
              if ((currentCallStatus === 5 || currentCallStatus === 20) && nextCallStatus === 10) {
                return currentCallStatus;
              }
              return nextCallStatus;
            });
          }
        } catch (err) {
          console.log("call status parse error", err);
        }
        return;
      }

      if (topic === conveyorTopic) {
        try {
          const payload = JSON.parse(data.toString());
          const conveyorTimeSeconds = Math.max(1, parseInt(payload.time, 10) || 1);

          if (dispenseTimerRef.current) {
            window.clearTimeout(dispenseTimerRef.current);
          }

          setDispensePhase('dispensing');
          dispenseTimerRef.current = window.setTimeout(() => {
            setDispensePhase('completed');
            dispenseTimerRef.current = null;
          }, conveyorTimeSeconds * 1000);
        } catch (err) {
          console.log("conveyor payload parse error", err);
        }
      }
    };

    client.on('message', onMessage);
    client.on("error", (err) => {
      console.log("Connection error:", err);
    });

    return () => {
      client.removeListener('message', onMessage);
      client.unsubscribe(localTopic);
      client.unsubscribe(callTopic);
      client.unsubscribe(conveyorTopic);
      if (dispenseTimerRef.current) {
        window.clearTimeout(dispenseTimerRef.current);
        dispenseTimerRef.current = null;
      }
    };
  }, [client, vm_id]);

  useEffect(() => {
    if (callStatus !== 5) {
      setDispensePhase('idle');
      if (dispenseTimerRef.current) {
        window.clearTimeout(dispenseTimerRef.current);
        dispenseTimerRef.current = null;
      }
    }
  }, [callStatus]);

  const showLoadingState = callStatus === null;
  const showCallingOperator = callStatus === 10;
  const showOperatorHold = callStatus === 20;
  const showCheckout = callStatus === 5;
  const showDispensingMessage = showCheckout && dispensePhase === 'dispensing';
  const showDispensedMessage = showCheckout && dispensePhase === 'completed';

    
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#0f1a14', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
          {showLoadingState ? (
            <Box sx={STATUS_CARD_SX}>
              <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', textAlign: 'center', fontSize: { xs: '2.2rem', md: '3rem' } }}>
                Connecting...
              </Typography>
            </Box>
          ) : showCallingOperator ? (
            <Box sx={STATUS_CARD_SX}>
              <Box
                sx={{
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  bgcolor: '#6ea544',
                  display: 'grid',
                  placeItems: 'center'
                }}
              >
                <CallMadeRoundedIcon sx={{ fontSize: 90, color: '#2c4d1d' }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em', fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                Calling operator . . . . . . . .
              </Typography>
            </Box>
          ) : showOperatorHold ? (
            <Box sx={STATUS_CARD_SX}>
              <Box
                sx={{
                  width: 180,
                  height: 180,
                  borderRadius: '50%',
                  bgcolor: '#6ea544',
                  display: 'grid',
                  placeItems: 'center'
                }}
              >
                <HourglassBottomRoundedIcon sx={{ fontSize: 100, color: '#2c4d1d' }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em', textAlign: 'center', fontSize: { xs: '2.2rem', md: '3.2rem' } }}>
                Please wait, Operator busy now
              </Typography>
            </Box>
          ) : showDispensingMessage ? (
            <Box sx={STATUS_CARD_SX}>
              <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em', textAlign: 'center', fontSize: { xs: '2.2rem', md: '3.2rem' } }}>
                Item dispensing, please wait...
              </Typography>
            </Box>
          ) : showDispensedMessage ? (
            <Box sx={STATUS_CARD_SX}>
              <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em', textAlign: 'center', fontSize: { xs: '2.2rem', md: '3.2rem' } }}>
                All items dispensed. Please pickup. Thank you.
              </Typography>
            </Box>
          ) : showCheckout ? (
            <Box sx={CHECKOUT_CARD_SX}>
              <Box
                sx={{
                  bgcolor: '#35501a',
                  px: 4,
                  py: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: '#f3f7ec',
                  borderBottom: '3px solid #2c4116',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: { xs: '2.6rem', md: '4rem' } }}>
                  Welcome !!
                </Typography>
                <Typography variant="subtitle1" sx={{ maxWidth: 460, textAlign: 'right', color: '#f3f7ec', fontSize: { xs: '1rem', md: '1.5rem' } }}>
                  Please check all your items thoroughly while ordering
                </Typography>
              </Box>

              <Box sx={{ p: 3.5 }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    bgcolor: '#5a7f33',
                    borderRadius: 0,
                    boxShadow: 'none',
                    border: '2px solid #d9e7c8',
                    minHeight: 360,
                  }}
                >
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#4c6e2c' }}>
                        <TableCell align="center" sx={{ color: '#e7f2d7', fontWeight: 800, fontSize: { xs: '1rem', md: '1.45rem' }, borderRight: '2px solid #d9e7c8' }}>Sl.</TableCell>
                        <TableCell align="left" sx={{ color: '#e7f2d7', fontWeight: 800, fontSize: { xs: '1rem', md: '1.45rem' }, borderRight: '2px solid #d9e7c8' }}>Item description</TableCell>
                        <TableCell align="right" sx={{ color: '#e7f2d7', fontWeight: 800, fontSize: { xs: '1rem', md: '1.45rem' }, borderRight: '2px solid #d9e7c8' }}>Rate</TableCell>
                        <TableCell align="center" sx={{ color: '#e7f2d7', fontWeight: 800, fontSize: { xs: '1rem', md: '1.45rem' }, borderRight: '2px solid #d9e7c8' }}>Qty</TableCell>
                        <TableCell align="right" sx={{ color: '#e7f2d7', fontWeight: 800, fontSize: { xs: '1rem', md: '1.45rem' } }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((row, index) => (
                        <TableRow key={`${row.desc}-${index}`}>
                          <TableCell align="center" sx={{ color: '#f7ff8a', fontStyle: 'italic', fontSize: { xs: '1rem', md: '1.35rem' }, borderRight: '2px solid #d9e7c8' }}>
                            {String(index + 1).padStart(2, '0')}
                          </TableCell>
                          <TableCell align="left" sx={{ color: '#f7ff8a', fontStyle: 'italic', fontSize: { xs: '1rem', md: '1.35rem' }, borderRight: '2px solid #d9e7c8' }}>
                            {row.desc}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#f7ff8a', fontStyle: 'italic', fontSize: { xs: '1rem', md: '1.35rem' }, borderRight: '2px solid #d9e7c8' }}>
                            {formatCurrency(row.unit)}
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#f7ff8a', fontStyle: 'italic', fontSize: { xs: '1rem', md: '1.35rem' }, borderRight: '2px solid #d9e7c8' }}>
                            {row.qty}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#f7ff8a', fontStyle: 'italic', fontSize: { xs: '1rem', md: '1.35rem' } }}>
                            {formatCurrency(row.price)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {emptyRows > 0 && Array.from({ length: emptyRows }).map((_, index) => (
                        <TableRow key={`empty-${index}`} sx={{ height: 44 }}>
                          <TableCell sx={{ borderRight: '2px solid #d9e7c8' }} />
                          <TableCell sx={{ borderRight: '2px solid #d9e7c8' }} />
                          <TableCell sx={{ borderRight: '2px solid #d9e7c8' }} />
                          <TableCell sx={{ borderRight: '2px solid #d9e7c8' }} />
                          <TableCell />
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={4} align="right" sx={{ color: '#f3f7ec', fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.8rem' }, borderTop: '2px solid #d9e7c8' }}>
                          Total
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#f7ff8a', fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.8rem' }, borderTop: '2px solid #d9e7c8' }}>
                          {formatCurrency(price_total)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    border: '2px solid #d9e7c8',
                    borderTop: 'none',
                    px: 2.5,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    color: '#f3f7ec',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.3rem', md: '2rem' } }}>
                    Amount
                  </Typography>
                  <Typography variant="h6" sx={{ fontStyle: 'italic', color: '#f7ff8a', fontSize: { xs: '1.3rem', md: '2rem' } }}>
                    {amountWords(price_total)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={STATUS_CARD_SX}>
              <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', textAlign: 'center', fontSize: { xs: '2.2rem', md: '3rem' } }}>
                Press call button to connect
              </Typography>
            </Box>
          )}
        </Box>
      );
}
